import CalendarPage from './CalendarPage'
import { RoleRoute } from '@/types'
import { Roles } from '@/auth/azure/Roles'
import RouterErrorHandler from '@/components/RouterErrorHandler'
import { defer, LoaderFunction } from 'react-router-typesafe'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { getAzureUserInformation } from '@/auth/azure/azureManager'
import { faCalendar } from '@fortawesome/free-solid-svg-icons'
import { user } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { CalendarRoom, CalendarUser, RoomResponseDTO } from '@/routes/calendar/CalendarPage.types'
import { generateColorByUserId } from '@/routes/calendar/CalendarPage.utils'

export const calendarPageRouteLoader = (async () => {
  const qsAdminApi = useQsAdminApiManager.getState().service
  const qsAdminApiClient = useQsAdminApiManager.getState().client

  const getImpiegati = await qsAdminApi.user().query((builder, impiegato) => {
    builder.select('id', 'fullname', 'email', 'reparto')
    builder.expanding('reparto', (repartoBuilder, reparto) => {
      repartoBuilder.select('id', 'nome')
    })
    builder.filter(
      impiegato.attivo
        .eq(true)
        .and(impiegato.azienda.props.p_iva.eq('01955220346'))
        .and(impiegato.contractor.eq(false)),
    )
    builder.orderBy(impiegato.fullname.asc())
  })

  const allEmployee: user[] = getImpiegati.data.value

  // Group employees by department
  const groupedByDepartment: Record<
    string,
    {
      fullname: string
      email: string | null
      items: CalendarUser[]
    }
  > = {}

  for (const impiegato of allEmployee) {
    const repartoName = impiegato.reparto?.nome || 'Unknown'
    if (!groupedByDepartment[repartoName]) {
      groupedByDepartment[repartoName] = { fullname: repartoName, email: null, items: [] }
    }
    groupedByDepartment[repartoName].items.push({
      userid: impiegato.id,
      fullname: impiegato.fullname,
      email: impiegato.email,
      color: generateColorByUserId(impiegato.id),
    })
  }

  const sortedDepartments = Object.values(groupedByDepartment)
    .sort((a, b) => a.fullname.localeCompare(b.fullname))
    .map((dept) => ({
      ...dept,
      items: dept.items.sort((a, b) => a.fullname.localeCompare(b.fullname)),
    }))

  const rooms = await qsAdminApiClient.post(`api/calendar/rooms`, {
    headers: {
      'Content-Type': 'application/json',
    },
    // Optionally
    data: {
      startDateTime: new Date().toISOString(),
      endDateTime: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
    },
  })

  // Sort rooms and add visual indicator for status
  const allRooms: RoomResponseDTO[] = rooms.data.data
  const sortedRooms: CalendarRoom[] = allRooms
    .map((room: RoomResponseDTO) => ({
      userid: room.id,
      fullname: room.fullName,
      email: room.email,
      status: room.status,
      isAvailable: room.isAvailable,
      isFakePerson: true,
      color: generateColorByUserId(
        Math.abs(
          room.id
            ?.toString()
            .split('')
            .reduce((acc, char) => acc + char.charCodeAt(0), 0) || 0,
        ),
      ),
    }))
    .sort((a: CalendarRoom, b: CalendarRoom) => a.fullname.localeCompare(b.fullname))

  // Create the hierarchical structure
  const calendarsGrouped = [
    {
      fullname: 'UTENTI',
      email: null,
      items: sortedDepartments,
    },
    {
      fullname: 'STANZE',
      email: null,
      items: sortedRooms,
    },
  ]

  const users: ((user | CalendarRoom) & { color?: string })[] = [
    ...allEmployee.map((item) => ({
      ...item,
      color: generateColorByUserId(item.id),
    })),
    ...sortedRooms.map((room) => ({
      ...room,
      // Generate color from email hash since rooms use email as userid
      color: generateColorByUserId(
        Math.abs(
          room.userid
            ?.toString()
            .split('')
            .reduce((acc, char) => acc + char.charCodeAt(0), 0) || 0,
        ),
      ),
    })),
  ]

  return defer({
    userInfo: await getAzureUserInformation(),
    users,
    calendarsGrouped,
    rooms: sortedRooms,
  })
}) satisfies LoaderFunction

const CalendarPageRoute: RoleRoute = {
  path: 'calendar',
  loader: calendarPageRouteLoader,
  element: <CalendarPage />,
  errorElement: <RouterErrorHandler />,
  mainMenuRoute: true,
  text: 'Calendario',
  faIcon: faCalendar,
  handle: {
    crumb: () => ({
      label: 'Calendario',
      icon: faCalendar,
    }),
  },
  allowedRoles: [
    Roles.GlobalAdministrator,
    Roles.Administrator,
    Roles.Marketing,
    Roles.TechDeveloper,
    Roles.Sales,
    Roles.Supervisor,
  ],
}

export default CalendarPageRoute
